import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, ViewEncapsulation } from '@angular/core';
import { Payment, Transaction } from '../../models';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { AlertService } from '../../services/alert.service';
import { TokenServiceService } from '../../services/token-service.service';
import { DepositsManageService } from '../../services/deposits-manage.service';
import { CopyToClipboardComponent } from '../../shared/components/copy-to-clipboard/copy-to-clipboard.component';
import { ALERT_BUTTONS_MESSAGES, ALERT_MESSAGES } from '../../core/constants/AlertMessages';
import { BANK_GU_IDS } from 'src/app/core/constants/payMethods-gu';
import { BANK_IDS } from 'src/app/core/constants/payMethods-cr';
import { EmailValidator } from 'src/app/tools/email-validator';
import { PayvalidaFeatureFlagService } from 'src/app/services/payvalida-feature-flag.service';
import { AUTOCOMPLETE_METHOD_PAY_TYPES, FLAGS_NAME } from 'src/app/core/constants/General-constants';
import { PatchValAdditionalInfoService } from 'src/app/services/patch-val-additional-info.service';
import { ALERT_ERRORS } from 'src/app/core/constants/Errors';

@Component({
    selector: 'app-transfer',
    templateUrl: './transfer.component.html',
    styleUrls: ['./transfer.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TransferComponent implements OnInit {
    @Input() info: Payment;
    @Input() currentConcept: any;
    @ViewChildren(CopyToClipboardComponent) copiesComponents: QueryList<CopyToClipboardComponent>;

    loading: boolean;
    transaction: Transaction;
    orderID: string;
    template: string;
    attemps = 0;
    showSpecial: boolean;
    @Output() CASH = new EventEmitter();
    @Output() buttonStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

    public emailForm: FormGroup;
    isGUtransfer: boolean;
    formPrivacyValid: boolean = false;
    buttonEnabled: boolean = false;
    rc_number: string;
    isLafiseTransfer: boolean = false;
    transferAndDepositsAutoCompleteFF: boolean = false;
    isEditableInfo: boolean = false;
    objectControlData = [
      {
          formControl: 'email',
          readOnly: false,
      },
    ];

    constructor(
        private readonly authService: AuthService,
        private readonly alertService: AlertService,
        private readonly tokenServiceService: TokenServiceService,
        private readonly depositManager: DepositsManageService,
        private readonly formBuilder: FormBuilder,
        private readonly featureFlagService: PayvalidaFeatureFlagService,
        private readonly patchValAdditionalInfoService: PatchValAdditionalInfoService
    ) {
        this.emailForm = this.formBuilder.group({
            email: ['', Validators.compose([Validators.required, EmailValidator.isValid])],
        });
    }

    ngOnInit() {
        this.onActivate();

        this.rc_number = this.info.concept[0].number;
        this.isGUtransfer = this.isGU(this.info.originCode);
        this.isLafiseTransfer = this.info.originCode === BANK_IDS.LAFISE_TRANSFER;
        this.orderID = this.authService.getOrderID();
        this.template = this.info.template;
        this.transaction = this.tokenServiceService.getAndDecodeToken();
        this.showSpecial = this.info.category === '7';

        if (this.showSpecial) {
            this.consultDepositAttemps();
        }

        this.featureFlagService.getFeatureFlagValue(FLAGS_NAME.TRANSFER_AND_DEPOSITS_AUTOCOMPLETE_FF, FLAGS_NAME.TRANSFER_AND_DEPOSITS_AUTOCOMPLETE_FF).subscribe((response: boolean) => {
          this.transferAndDepositsAutoCompleteFF = response;

          const additionalInfo = this.tokenServiceService.exitsAdditionalInformation(this.transaction);

          if (additionalInfo && this.transferAndDepositsAutoCompleteFF) return this.disabledItems(additionalInfo);
        });
    }

     /**
     *
     * @description {isControlReadOnly} function to check if form fields are read-only
     * @param controlName
     * @returns
     */
     isControlReadOnly(controlName: string): boolean {
      const readOnlyControl = this.objectControlData.find((m) => m.formControl === controlName);

      return readOnlyControl ? readOnlyControl.readOnly : false;
    }

    /**
    *
    * @description {disabledItems} Function to disabled items to checkout form
    */
    disabledItems(additionalInfo) {
        try {
            this.isEditableInfo = this.tokenServiceService.isEditableInfo(additionalInfo);

            return this.patchValAdditionalInfoService.patchValAdditionalInfo(
                this.isEditableInfo,
                additionalInfo,
                this.emailForm,
                AUTOCOMPLETE_METHOD_PAY_TYPES.DEPOSITS_AND_TRANSFERS,
                this.objectControlData,
            );
        } catch (_) {
            this.alertService.showAlert(ALERT_ERRORS.GET_TOKEN_DATA_ERROR);
        }
    }

    changeMethod() {
        this.CASH.emit();
    }

    getValCashAccInfo() {
        let defaultTextAccInfo = 'PAYVALIDA COSTA RICA S.A\n3-101-727969\ncontacto@payvalida.com';
        if (this.info.code == 271) defaultTextAccInfo = 'PAYVALIDA COSTA RICA S.A\n3-101-727969\ninfo.cr@payvalida.com';
        return defaultTextAccInfo;
    }

    onActivate() {
        window.scroll(0, 0);
    }

    consultDepositAttemps() {
        this.loading = true;
        this.depositManager.consultOrder(this.transaction.ORDER_CODE.toString()).subscribe((res: any) => {
            this.attemps = res.attemps;
            this.loading = false;
        });
    }

    get email() {
        return this.emailForm.get('email').value;
    }
    /**
     * @description {sendEmail} Function to send transaction email with rate limit
     */
    sendEmail() {
        this.loading = true;
        if (this.emailForm.valid) {
            this.depositManager.sendEmailWithLimit(this.transaction.ORDER_CODE.toString(), this.email).subscribe({
                next: (response) => {
                    if (response) {
                        this.alertService.showPopup(ALERT_MESSAGES.EMAIL_SENT, ALERT_MESSAGES.EMAIL_SENT_MESSAGE, 'success', ALERT_BUTTONS_MESSAGES.THANKS);
                        this.loading = false;
                        return;
                    }
                    this.alertService.showAlertProblem();
                },
                error: () => {
                    this.loading = false;
                    this.alertService.showPopup(ALERT_MESSAGES.EMAIL_NOT_SENT, ALERT_MESSAGES.EMAIL_NOT_SENT_RATE_LIMIT, 'error', ALERT_BUTTONS_MESSAGES.THANKS);
                },
            });
        }
    }

    /**
     * @description returns if the commercial code belongs to those of Guatemala BAM paymethods
     */
    isGU(code: number) {
        return code === BANK_GU_IDS.BAM_TRANSFER || code === BANK_GU_IDS.BI_TRANSFER;
    }

    /**
     *
     * @description {formPrivacyUpdate} Function that obtains the event of the validation of the child component
     * @param valid
     */
    formPrivacyUpdate(valid: boolean) {
        this.formPrivacyValid = valid;
        this.enableButton();
    }

    /**
     *
     * @description {enableButton} Function that validates and emit the event of the validation of the child component
     * @param valid
     */
    enableButton() {
        this.buttonEnabled = this.emailForm.valid && this.formPrivacyValid;
        this.buttonStateChange.emit(this.buttonEnabled);
    }
}
